import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Bandeau, Zone } from './Champs'
import { jour } from './Bail'
import { useReputation } from './BlocReputation'
import { ErreurApi } from '../api'
import { EcranErreur } from './Erreurs'
import {
  ficheVote,
  mesVotes,
  voter,
  type FicheVote,
  type ResultatVote,
  type ValeurVote,
  type VoteEnAttente,
} from '../apiBail'
import { monLifestyle, type Reponse } from '../apiMatching'
import { interpoler, messageErreur, TEXTES, type Langue } from '../i18n'
import { Bloc, Bouton, EtatVide, Icone, LienBouton, Puce, SqueletteListe, SquelettePage } from '../ui'

type Textes = (typeof TEXTES)['fr']

const CHOIX: readonly ValeurVote[] = ['For', 'Neutral', 'Against']

/**
 * Avis du foyer — revu le 15/09/2026 sur les maquettes `avis-du-foyer.html` et `vote.html`.
 *
 * <b>D28, les quatre visibilités du vote, rendues lisibles et pas seulement respectées.</b> Le
 * colocataire lit le candidat — son nom, son âge, sa situation, son message, ses traits de vie —
 * et <b>ni revenu, ni garant, ni employeur</b> : le serveur ne les envoie pas, l'écran n'a aucun
 * moyen de les montrer, et il le dit. Sa réponse est lue sans son nom ; le candidat ne saura jamais
 * ce que le foyer a répondu.
 *
 * <b>Une consultation close reste lisible</b> (15/09/2026) : ce que j'ai répondu, et l'issue du
 * foyer — favorable, défavorable, partagé, ou <i>sans avis</i> faute de quorum, ce qui n'est pas un
 * refus. Une réponse qui disparaissait le jour de la clôture se lisait comme une perte.
 */
export function MesVotes({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]
  const [liste, setListe] = useState<VoteEnAttente[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    void mesVotes()
      .then(setListe)
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [langue])

  return (
    <section className="vote">
      <div className="page-entete">
        <h2>{t.voteFoyer}</h2>
        <p>{t.voteFoyerIntro}</p>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {liste === null && erreur === null && <SqueletteListe />}

      {liste !== null && (
        <div className="pile">
          {liste.map((attente) =>
            attente.outcome === 'Pending' ? (
              <ConsultationOuverte key={attente.sessionId} t={t} langue={langue} attente={attente} />
            ) : (
              <ConsultationClose key={attente.sessionId} t={t} langue={langue} attente={attente} />
            ),
          )}

          <EtatVide icone="balance" titre={liste.length === 0 ? t.voteFoyerAucun : t.voteFoyerAucunAutre} action={<LienBouton to="/foyer" apparence="secondaire">{t.voteFoyerRevenirFoyer}</LienBouton>}>
            {t.voteFoyerAucunAide}
          </EtatVide>
        </div>
      )}
    </section>
  )
}

function ConsultationOuverte({ t, langue, attente }: { t: Textes; langue: Langue; attente: VoteEnAttente }) {
  return (
    <Bloc
      titre={
        <div>
          <h3>{titreCandidat(t, attente.seekerDisplayName, attente.listingTitle)}</h3>
          <p className="secondaire">{interpoler(t.voteFoyerOuverteLe, { date: jour(langue, attente.openedAt) })}</p>
        </div>
      }
      actions={<Puce ton="attention">{interpoler(t.voteFoyerRepondreJusquAu, { date: jour(langue, attente.closesAt) })}</Puce>}
    >
      <div className="pile">
        <div className="rangee rangee--haut vote__candidat">
          <span className="avatar avatar--lg" aria-hidden="true">
            {initiales(attente.seekerDisplayName)}
          </span>
          <div className="pile pile--serree vote__candidat-corps">
            <p>
              <b>{presentation(t, langue, attente.seekerAge, attente.occupation)}</b>
            </p>
            {attente.motivationMessage !== null && <p className="secondaire">« {attente.motivationMessage} »</p>}
          </div>
        </div>

        <Bandeau ton="info" icone={<Icone nom="bouclier" />}>
          {t.voteFoyerNiRevenu}
        </Bandeau>

        {attente.myVote !== 'NotCast' && (
          <p className="statut-point statut-point--succes">
            {interpoler(t.voteFoyerVotreReponse, { reponse: libelleChoix(t, attente.myVote).toLocaleLowerCase(langue) })}
            {attente.myComment !== null && ` — « ${attente.myComment} »`}
          </p>
        )}

        <div className="rangee rangee--serree">
          <LienBouton to={`/avis-du-foyer/${attente.sessionId}`} taille="sm">
            {attente.myVote === 'NotCast' ? t.voteFoyerDonnerAvis : t.voteFoyerChangerAvis}
          </LienBouton>
          <LienBouton to={`/avis-du-foyer/${attente.sessionId}`} apparence="discret" taille="sm">
            {t.voteFoyerVoirFiche}
          </LienBouton>
        </div>
      </div>
    </Bloc>
  )
}

function ConsultationClose({ t, langue, attente }: { t: Textes; langue: Langue; attente: VoteEnAttente }) {
  const repondu = attente.myVote !== 'NotCast'

  return (
    <div className="est-estompee">
      <Bloc
        titre={
          <div>
            <h3>{titreCandidat(t, attente.seekerDisplayName, attente.listingTitle)}</h3>
            <p className="secondaire">{interpoler(t.voteFoyerCloseLe, { date: jour(langue, attente.closesAt) })}</p>
          </div>
        }
        actions={<Puce ton={repondu ? 'succes' : undefined}>{repondu ? t.voteFoyerVousAvezRepondu : t.voteFoyerSansReponse}</Puce>}
      >
        {repondu && (
          <p className="statut-point statut-point--succes">
            {interpoler(t.voteFoyerVotreReponse, { reponse: libelleChoix(t, attente.myVote).toLocaleLowerCase(langue) })}
            {attente.myComment !== null && ` — « ${attente.myComment} »`}
          </p>
        )}
        <p className="tertiaire vote__issue">
          {interpoler(t.voteFoyerResultat, { issue: libelleIssue(t, attente.outcome) })} {t.voteFoyerCandidatIgnore}
        </p>
      </Bloc>
    </div>
  )
}

/**
 * La fiche du candidat, et le vote.
 *
 * <b>Ce que cet écran n'affiche pas est le sujet</b> (conception L6, §1.2-c) : ni tranche de
 * revenu, ni garant, ni employeur, ni établissement. Le serveur ne les envoie pas ; l'écran n'a
 * donc aucun moyen de les montrer, même par erreur — c'est la seule forme de protection qui tienne
 * dans la durée. <b>« Son mode de vie, face au vôtre »</b> compare ses réponses à ce que je tolère,
 * trait par trait : c'est ce que le moteur compare, et c'est ce qui aide à répondre.
 */
export function FicheVoteEcran({ langue }: { langue: Langue }) {
  const { id } = useParams<{ id: string }>()
  const t = TEXTES[langue]
  const [fiche, setFiche] = useState<FicheVote | null>(null)
  const [mesReponses, setMesReponses] = useState<Reponse[] | null>(null)
  const [choix, setChoix] = useState<ValeurVote>('NotCast')
  const [commentaire, setCommentaire] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  /** La cause de l'échec du chargement — un écran entier, là où un vote raté est un bandeau. */
  const [echec, setEchec] = useState<unknown>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const reputation = useReputation(fiche === null ? null : { nature: 'utilisateur', id: fiche.seekerUserId }, langue)

  const charger = useCallback(() => {
    if (id === undefined) {
      return
    }

    setEchec(null)

    void ficheVote(id)
      .then((valeur) => {
        setFiche(valeur)
        setChoix(valeur.myVote)
        setCommentaire(valeur.myComment ?? '')
      })
      .catch((cause: unknown) => setEchec(cause ?? new Error()))

    // Mon questionnaire, pour la comparaison ; sans lui, la fiche montre ses réponses seules.
    void monLifestyle()
      .then((profil) => setMesReponses(profil.exists ? profil.answers : []))
      .catch(() => setMesReponses([]))
  }, [id])

  useEffect(charger, [charger])

  const envoyer = useCallback(async () => {
    if (id === undefined || choix === 'NotCast') {
      return
    }

    setErreur(null)
    setEnvoi(true)

    try {
      await voter(id, choix, commentaire.trim() === '' ? null : commentaire.trim())
      setMessage(t.voteFoyerEnvoye)
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }, [choix, commentaire, id, langue, t.voteFoyerEnvoye])

  // Revu le 16/09/2026 (`docs/ui/pwa/introuvable.html`) : une consultation que l'API ne rend pas
  // — inconnue, ou d'un foyer qui n'est pas le mien — est un état vide, avec les sorties du foyer.
  if (echec !== null && fiche === null) {
    return (
      <section className="vote vote--fiche">
        <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
          <Link to="/avis-du-foyer">{t.voteFoyer}</Link>
          <Icone nom="chevron" taille="sm" miroir />
          <span aria-current="page">{t.erreurVoteTitre}</span>
        </nav>
        <EcranErreur cause={echec} objet="vote" onReessayer={charger} />
      </section>
    )
  }

  if (fiche === null) {
    return <SquelettePage />
  }

  const ouverte = fiche.outcome === 'Pending'
  const note = new Intl.NumberFormat(langue, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const nombre = new Intl.NumberFormat(langue)

  return (
    <section className="vote vote--fiche">
      <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
        <Link to="/avis-du-foyer">{t.voteFoyer}</Link>
        <Icone nom="chevron" taille="sm" miroir />
        <span aria-current="page">{fiche.seekerDisplayName ?? '—'}</span>
      </nav>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {message !== null && <Bandeau ton="succes">{message}</Bandeau>}

      <Bloc>
        <div className="pile">
          <div className="rangee rangee--haut vote__candidat">
            <span className="avatar avatar--lg" aria-hidden="true">
              {initiales(fiche.seekerDisplayName)}
            </span>
            <div className="pile pile--serree vote__candidat-corps">
              <h2 className="vote__nom">{fiche.seekerDisplayName ?? '—'}</h2>
              <p className="secondaire">
                {presentation(t, langue, fiche.seekerAge, fiche.occupation)}
                {fiche.moveInWindowStart !== null &&
                  ` · ${interpoler(t.voteFoyerAPartirDu, { date: jour(langue, fiche.moveInWindowStart) })}`}
                {fiche.desiredDurationMonths !== null &&
                  ` · ${interpoler(t.voteFoyerDureeMois, { n: fiche.desiredDurationMonths }, langue)}`}
              </p>
              {reputation.reputation !== null && reputation.reputation.isDisplayable && (
                <p className="note vote__note">
                  <Icone nom="etoile" taille="sm" />
                  <span className="note__valeur">{note.format(reputation.reputation.displayedRating ?? 0)}</span>
                  <span className="note__compte">
                    · {nombre.format(reputation.reputation.reviewCount)} {t.voteFoyerAvisColocataire}
                  </span>
                </p>
              )}
            </div>
          </div>

          {fiche.motivationMessage !== null && <p className="vote__message">« {fiche.motivationMessage} »</p>}

          {fiche.lifestyleTraits.length > 0 && (
            <div>
              <h3 className="vote__sous-titre">{mesReponses !== null && mesReponses.length > 0 ? t.voteFoyerFaceAuVotre : t.voteFoyerSonModeDeVie}</h3>
              <div className="criteres">
                {fiche.lifestyleTraits.map((trait) => {
                  const mienne = mesReponses?.find((reponse) => reponse.traitCode === trait.traitCode)
                  const ecart = ecartAvec(trait.selfValue, mienne)

                  return (
                    <div key={trait.traitCode} className="jauge">
                      <div className="jauge__ligne">
                        <span>{trait.label}</span>
                        <span>{ecart === null ? valeurSeule(t, trait.selfValue) : libelleEcart(t, ecart, langue)}</span>
                      </div>
                      <div className="jauge__piste">
                        <div
                          className={`jauge__valeur${ecart !== null && ecart >= 2 ? ' jauge__valeur--danger' : ecart === 1 ? ' jauge__valeur--attention' : ''}`}
                          style={{ inlineSize: `${ecart === null ? largeurSeule(trait.selfValue) : Math.max(10, 100 - ecart * 25)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <Bandeau ton="info" icone={<Icone nom="bouclier" />}>
            {t.voteFoyerNiRevenuFiche}
          </Bandeau>
        </div>
      </Bloc>

      {ouverte && (
        <form
          className="bloc"
          onSubmit={(evenement) => {
            evenement.preventDefault()
            void envoyer()
          }}
        >
          <h2 className="vote__sous-titre">{t.voteFoyerVotreAvis}</h2>

          <div className="pile pile--serree vote__choix" role="radiogroup" aria-label={t.voteFoyerVotreAvis}>
            {CHOIX.map((valeur) => (
              <label key={valeur} className="radio bloc bloc--sourd vote__option">
                <input type="radio" name="avis" checked={choix === valeur} onChange={() => setChoix(valeur)} />
                <span>
                  <b>{libelleChoix(t, valeur)}</b> — {aideChoix(t, valeur)}
                </span>
              </label>
            ))}
          </div>

          <Zone
            id="vote-commentaire"
            libelle={t.voteFoyerCommentaire}
            valeur={commentaire}
            aide={t.voteFoyerCommentaireAide}
            onChange={setCommentaire}
          />

          <div className="rangee rangee--espace vote__pied">
            <p className="tertiaire">{interpoler(t.voteFoyerChangerJusquAu, { date: jour(langue, fiche.closesAt) })}</p>
            <Bouton type="submit" charge={envoi} desactive={choix === 'NotCast'}>
              {t.voteFoyerEnvoyer}
            </Bouton>
          </div>
        </form>
      )}

      <Bloc variante="sourd" titre={<h3>{t.voteFoyerAvis}</h3>}>
        <p className="secondaire">
          {ouverte ? t.voteFoyerResultatPlusTard : interpoler(t.voteFoyerResultat, { issue: libelleIssue(t, fiche.outcome) })}
        </p>
        <p className="tertiaire vote__issue">{t.voteFoyerSansQuorumAide}</p>
      </Bloc>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Comparaison et libellés
// ---------------------------------------------------------------------------

/**
 * L'écart entre ce que le candidat fait et ce que je tolère — à défaut, ce que je fais. Nul quand
 * l'un des deux n'a pas répondu : on ne compare pas à une absence.
 */
function ecartAvec(candidat: number | null, mienne: Reponse | undefined): number | null {
  if (candidat === null || mienne === undefined) return null

  const reference = mienne.toleranceValue ?? mienne.selfValue

  return reference === null ? null : Math.abs(candidat - reference)
}

function libelleEcart(t: Textes, ecart: number, langue: Langue): string {
  if (ecart === 0) return t.voteFoyerCompatible
  if (ecart === 1) return t.voteFoyerEcartUn

  return interpoler(t.voteFoyerEcartN, { n: ecart }, langue)
}

/** Sans mon questionnaire : la valeur seule sur son échelle, de 1 à 5. */
function valeurSeule(t: Textes, valeur: number | null): string {
  return valeur === null ? t.voteFoyerNonRenseigne : `${valeur + 1} / 5`
}

function largeurSeule(valeur: number | null): number {
  return valeur === null ? 0 : ((valeur + 1) / 5) * 100
}

function titreCandidat(t: Textes, nom: string | null, annonce: string | null): string {
  return annonce === null
    ? (nom ?? '—')
    : interpoler(t.voteFoyerCandidatPour, { nom: nom ?? '—', annonce })
}

/** « 28 ans · salarié » — chaque morceau seulement s'il est connu. */
function presentation(t: Textes, langue: Langue, age: number | null, occupation: string | null): string {
  const morceaux: string[] = []

  if (age !== null) morceaux.push(interpoler(t.voteFoyerAns, { n: age }, langue))
  if (occupation !== null) morceaux.push(occupation)

  return morceaux.length === 0 ? t.voteFoyerFiche : morceaux.join(' · ')
}

function libelleChoix(t: Textes, valeur: ValeurVote): string {
  switch (valeur) {
    case 'For':
      return t.voteFoyerPour
    case 'Against':
      return t.voteFoyerContre
    case 'Neutral':
      return t.voteFoyerNeutre
    default:
      return '—'
  }
}

function aideChoix(t: Textes, valeur: ValeurVote): string {
  switch (valeur) {
    case 'For':
      return t.voteFoyerPourAide
    case 'Against':
      return t.voteFoyerContreAide
    default:
      return t.voteFoyerNeutreAide
  }
}

function libelleIssue(t: Textes, issue: ResultatVote): string {
  switch (issue) {
    case 'Favorable':
      return t.voteFoyerFavorable
    case 'Unfavorable':
      return t.voteFoyerDefavorable
    case 'Split':
      return t.voteFoyerPartage
    case 'NoQuorum':
      return t.voteFoyerSansQuorumCourt
    case 'Cancelled':
      return t.voteFoyerAnnulee
    default:
      return t.voteFoyerEnCours
  }
}

function initiales(nom: string | null): string {
  if (nom === null) return '?'

  return nom
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase())
    .join('')
}
